import {
  calendarViewOptions,
  controlsForCalendarView
} from "../components/tableControls/forecastTableControlUtils";
import type {
  ForecastTableControlOptions,
  ForecastTableControlValues
} from "../components/tableControls/forecastTableControlValues";
import type { ForecastMetaDataType } from "../hooks/useFetchForecastMetaData";
import type { SavedViewPayload, ViewDataShape } from "./savedViewPayload";

/** The live bar a data shape is applied to, and compared against. */
export interface ShapeEnvironment {
  controls: ForecastTableControlValues;
  /** The option lists the period and planning year are checked against. */
  options: Pick<ForecastTableControlOptions, "timePeriod" | "planningYear">;
}

/**
 * Whether the bar can take the shape's ``key`` as it stands. Callers check
 * the value is present; this answers only whether it can land.
 *
 * One predicate for the apply and the dirty check, so a key the apply
 * refuses never reads as drift — a view the user just opened would
 * otherwise show the drift dot with no way to clear it.
 */
export function canAssertShapeKey(
  key: keyof ViewDataShape,
  value: string,
  env: ShapeEnvironment
): boolean {
  switch (key) {
    case "timePeriod":
      return env.options.timePeriod.includes(
        value as ForecastTableControlValues["timePeriod"]
      );
    case "planningYear":
      return env.options.planningYear.some((option) => option.value === value);
    // A grid opens on the source its payload carries (`main_intake_source`),
    // whatever source a view was saved on.
    case "intake":
      return false;
    case "calendarView":
    case "planBy":
      return true;
  }
}

/**
 * Controls a payload asserts, merged onto ``live.controls``.
 *
 * The option lists the period and planning year are checked against are
 * derived from the calendar family and the metadata, not read from state:
 * the merge runs inside a functional updater, where the option state of the
 * render that queued it may already be stale (``useViewState``).
 */
export function mergeDataShape(
  payload: SavedViewPayload,
  live: {
    controls: ForecastTableControlValues;
    metaData: ForecastMetaDataType | undefined;
  }
): ForecastTableControlValues {
  const shape = payload.dataShape;
  let controls = { ...live.controls };

  if (shape?.calendarView && shape.calendarView !== controls.calendarView) {
    controls = controlsForCalendarView(
      shape.calendarView,
      controls,
      live.metaData
    );
  }
  // The calendar cascade rewrites the option lists, so the rest is checked
  // against the environment it leaves behind.
  const env: ShapeEnvironment = {
    controls,
    options: calendarViewOptions(controls.calendarView, live.metaData)
  };
  if (
    shape?.timePeriod &&
    canAssertShapeKey("timePeriod", shape.timePeriod, env)
  ) {
    controls.timePeriod = shape.timePeriod;
  }
  if (shape?.planBy) controls.planBy = shape.planBy;
  if (
    shape?.planningYear &&
    canAssertShapeKey("planningYear", shape.planningYear, env)
  ) {
    controls.planningYear = shape.planningYear;
  }
  if (payload.layout) {
    if (payload.layout.weekLabels) {
      controls.weekLabels = payload.layout.weekLabels;
    }
    controls.gridStyleGrouping = payload.layout.gridStyleGrouping;
  }
  return controls;
}
